import { Duration, Timestamp } from '@bufbuild/protobuf';
import { generateTypes } from '@evmos/eip712';
import { TxContext, TxPayload, createTransactionPayload } from '@evmos/transactions';
import { EIP712TypedData } from '@evmos/transactions/dist/messages/base';
import { fromDuration, toTimestamp } from 'cosmjs-types/helpers';
import { MsgCreatePlan } from '../ts-client/tx';
import { MsgCreatePlan as MsgCreatePlanProto } from '../proto/tx_pb';
import { BondingCurve as BondingCurveProto, IncentivePlanParams as IncentivePlanParamsProto } from '../proto/iro_pb';
import { toFixed } from '@cosmjs/math';

const getFixedIsoDate = (date: Date): string => {
  return date.toISOString().replace('.000Z', 'Z');
};

const MSG_CREATE_PLAN_TYPES = (params: MsgCreatePlan) => ({
  MsgValue: [
    { name: 'owner', type: 'string' },
    { name: 'rollapp_id', type: 'string' },
    { name: 'allocated_amount', type: 'string' },
    ...(params.bondingCurve ? [ { name: 'bonding_curve', type: 'TypeBondingCurve' } ] : []),
    ...(params.startTime ? [ { name: 'start_time', type: 'string' } ] : []),
    ...(params.iroPlanDuration ? [ { name: 'iro_plan_duration', type: 'int64' } ] : []),
    ...(params.incentivePlanParams ? [ { name: 'incentive_plan_params', type: 'TypeIncentivePlanParams' } ] : []),
  ],
  ...(params.bondingCurve && {
    TypeBondingCurve: [
      { name: 'M', type: 'string' },
      { name: 'N', type: 'string' },
      { name: 'C', type: 'string' },
    ],
  }),
  ...(params.incentivePlanParams && {
    TypeIncentivePlanParams: [
      ...(params.incentivePlanParams.startTimeAfterSettlement ?
        [ { name: 'start_time_after_settlement', type: 'int64' } ] : []),
      { name: 'num_epochs_paid_over', type: 'uint64' },
    ],
  }),
});

export const createTxMsgCreatePlan = (context: TxContext, params: MsgCreatePlan): TxPayload => {
  const typedData = createEIP712MsgCreatePlan(context, params);
  const cosmosMsg = createCosmosMsgCreatePlan(context, params);
  return createTransactionPayload(context, typedData, cosmosMsg);
};

const createEIP712MsgCreatePlan = (context: TxContext, params: MsgCreatePlan): EIP712TypedData => {
  const types = generateTypes(MSG_CREATE_PLAN_TYPES(params));
  const message = createMsgCreatePlan(params);
  return { types, message };
};

const createCosmosMsgCreatePlan = (context: TxContext, params: MsgCreatePlan): any => {
  const message = new MsgCreatePlanProto({
    owner: params.owner,
    rollappId: params.rollappId,
    allocatedAmount: params.allocatedAmount,
    bondingCurve: !params.bondingCurve ? undefined : new BondingCurveProto({
      M: params.bondingCurve.M,
      N: params.bondingCurve.N,
      C: params.bondingCurve.C,
    }),
    startTime: !params.startTime ? undefined : toTimestamp(params.startTime),
    iroPlanDuration: params.iroPlanDuration,
    incentivePlanParams: !params.incentivePlanParams ? undefined : new IncentivePlanParamsProto({
      numEpochsPaidOver: params.incentivePlanParams.numEpochsPaidOver,
      startTimeAfterSettlement: params.incentivePlanParams.startTimeAfterSettlement,
    }),
  });
  return { message, path: MsgCreatePlanProto.typeName };
};

function createMsgCreatePlan(params: MsgCreatePlan): object | object[] {
  return {
    type: 'iro/CreatePlan',
    value: {
      owner: params.owner,
      rollapp_id: params.rollappId,
      allocated_amount: params.allocatedAmount,
      bonding_curve: !params.bondingCurve ? undefined : {
        M: toFixed(Number(params.bondingCurve.M) / Math.pow(10, 18)),
        N: toFixed(Number(params.bondingCurve.N) / Math.pow(10, 18)),
        C: toFixed(Number(params.bondingCurve.C) / Math.pow(10, 18)),
      },
      start_time: !params.startTime ? undefined : getFixedIsoDate(params.startTime),
      iro_plan_duration: !params.iroPlanDuration  ? undefined : fromDuration(params.iroPlanDuration),
      incentive_plan_params: !params.incentivePlanParams ? undefined : {
        start_time_after_settlement: !params.incentivePlanParams.startTimeAfterSettlement ? undefined :
          fromDuration(params.incentivePlanParams.startTimeAfterSettlement),
        num_epochs_paid_over: params.incentivePlanParams.numEpochsPaidOver,
      },
    },
  };
}

